import React, { useState, useEffect } from 'react';
import VPSPackagesList from '../components/vps/VPSPackagesList';
import SEOHelmet from '../components/SEOHelmet';

interface VPSPlan {
  id: string;
  name: string;
  description: string;
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
  bandwidth_gb: number;
  monthly_price: string;
  hourly_price: string;
  features: {
    ssd_storage: boolean;
    backup_included: boolean;
    monitoring_included: boolean;
    load_balancer: boolean;
    dedicated_ip: boolean;
    priority_support?: boolean;
  };
  is_active: boolean;
}

const VPS: React.FC = () => {
  const [vpsPlans, setVpsPlans] = useState<VPSPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVPSPlans();
  }, []);

  const fetchVPSPlans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/vps/plans`);
      if (response.ok) {
        const data = await response.json();
        const activePlans = (data.data || []).filter((plan: VPSPlan) => plan.is_active);
        setVpsPlans(activePlans);
      }
    } catch (error) {
      console.error('Failed to fetch VPS plans:', error);
    } finally {
      setLoading(false);
    }
  };
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "VPS Hosting Services",
    "description": "High-performance Virtual Private Servers with full root access, SSD storage, and instant provisioning.",
    "brand": {
      "@type": "Brand",
      "name": "12and22"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "19.99",
      "highPrice": "159.99"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHelmet
        title="VPS Hosting - High Performance Virtual Private Servers | 12and22"
        description="Get powerful VPS hosting with full root access, SSD storage, and instant provisioning starting at $19.99/month. 99.9% uptime, scalable resources, and 24/7 expert support."
        keywords="VPS hosting, virtual private server, cloud VPS, Linux VPS, Windows VPS, managed VPS, SSD VPS, root access, dedicated resources, 12and22"
        canonicalUrl="https://12and22.com/vps"
        structuredData={structuredData}
      />
      {/* Hero Section with modern design */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-8 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full text-gray-700">
              <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium">Most Popular Choice</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-8 text-gray-900">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">VPS</span> <span className="text-primary-navy">Hosting</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-4xl mx-auto">
              Powerful Virtual Private Servers with full root access and dedicated resources. 
              Scale your applications with enterprise-grade infrastructure and complete control.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <a
                href="#packages"
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                <span className="relative z-10">Choose Your VPS</span>
              </a>
              
              <a
                href="#features"
                className="border-2 border-orange-400 text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gradient-to-r hover:from-orange-400 hover:to-yellow-500 hover:text-white hover:border-transparent transition-all duration-200"
              >
                Compare Plans
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-sm max-w-4xl mx-auto">
              {[
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>, 
                  text: 'Full Root Access', 
                  subtext: 'Complete Control' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>, 
                  text: 'SSD Storage', 
                  subtext: 'Ultra Fast' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>, 
                  text: 'Instant Deploy', 
                  subtext: 'Ready in Minutes' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>, 
                  text: 'Scalable Resources', 
                  subtext: 'Upgrade Anytime' 
                }
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className={`w-10 h-10 ${index === 1 || index === 2 ? 'bg-gradient-to-br from-orange-400 to-yellow-500' : 'bg-gray-100'} rounded-lg flex items-center justify-center ${index === 1 || index === 2 ? 'text-white' : 'text-gray-600'}`}>
                    {item.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{item.text}</div>
                    <div className="text-xs text-gray-500">{item.subtext}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <div id="packages">
        <VPSPackagesList />
      </div>

      {/* Features Comparison Section */}
      <section id="features" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">Detailed Comparison</span>
            </div>
            
            <h2 className="text-4xl font-black text-gray-900 mb-4">
              Compare VPS Plans
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find the perfect VPS plan that matches your requirements and budget
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl shadow-xl relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none rounded-2xl"></div>
            
            <div className="relative z-10 overflow-x-auto">
              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading VPS plans...</p>
                </div>
              ) : vpsPlans.length === 0 ? (
                <div className="p-12 text-center">
                  <p className="text-gray-600">No VPS plans available at the moment.</p>
                </div>
              ) : (
                <>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left p-6 font-bold text-gray-900">Features</th>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <th key={plan.id} className={`text-center p-6 ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            {index === 1 && (
                              <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-orange-400 to-yellow-500 text-white text-xs font-bold rounded-full mb-2">
                                MOST POPULAR
                              </div>
                            )}
                            <div className="text-lg font-bold text-gray-900">{plan.name}</div>
                            <div className="text-sm text-gray-500 mt-1">{plan.description}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* CPU Cores */}
                      <tr className="border-b border-gray-100">
                        <td className="p-6 font-medium text-gray-900">CPU Cores</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            <span className="text-gray-700">{plan.cpu_cores} vCPU{plan.cpu_cores > 1 ? 's' : ''}</span>
                          </td>
                        ))}
                      </tr>

                      {/* RAM */}
                      <tr className="border-b border-gray-100 bg-white bg-opacity-50">
                        <td className="p-6 font-medium text-gray-900">RAM</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            <span className="text-gray-700">{Math.round(plan.memory_mb / 1024)} GB</span>
                          </td>
                        ))}
                      </tr>

                      {/* SSD Storage */}
                      <tr className="border-b border-gray-100">
                        <td className="p-6 font-medium text-gray-900">SSD Storage</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            <span className="text-gray-700">{plan.disk_gb} GB</span>
                          </td>
                        ))}
                      </tr>

                      {/* Bandwidth */}
                      <tr className="border-b border-gray-100 bg-white bg-opacity-50">
                        <td className="p-6 font-medium text-gray-900">Bandwidth</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            <span className="text-gray-700">
                              {plan.bandwidth_gb >= 1000 ? `${Math.round(plan.bandwidth_gb / 1000)} TB` : `${plan.bandwidth_gb} GB`}
                            </span>
                          </td>
                        ))}
                      </tr>

                      {/* Features from database */}
                      <tr className="border-b border-gray-100">
                        <td className="p-6 font-medium text-gray-900">SSD Storage</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            {plan.features.ssd_storage ? (
                              <svg className="w-6 h-6 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-gray-100 bg-white bg-opacity-50">
                        <td className="p-6 font-medium text-gray-900">Backups Included</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            {plan.features.backup_included ? (
                              <svg className="w-6 h-6 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-gray-100">
                        <td className="p-6 font-medium text-gray-900">Monitoring</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            {plan.features.monitoring_included ? (
                              <svg className="w-6 h-6 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-gray-100 bg-white bg-opacity-50">
                        <td className="p-6 font-medium text-gray-900">Load Balancer</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            {plan.features.load_balancer ? (
                              <svg className="w-6 h-6 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-gray-100">
                        <td className="p-6 font-medium text-gray-900">Dedicated IP</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            {plan.features.dedicated_ip ? (
                              <svg className="w-6 h-6 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-gray-100 bg-white bg-opacity-50">
                        <td className="p-6 font-medium text-gray-900">Priority Support</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            {plan.features.priority_support ? (
                              <svg className="w-6 h-6 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </td>
                        ))}
                      </tr>

                      {/* Price */}
                      <tr className="border-b border-gray-100">
                        <td className="p-6 font-medium text-gray-900">Monthly Price</td>
                        {vpsPlans.slice(0, 3).map((plan, index) => (
                          <td key={plan.id} className={`p-6 text-center ${index === 1 ? 'bg-gradient-to-b from-orange-50 to-transparent' : ''}`}>
                            <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
                              ${parseFloat(plan.monthly_price).toFixed(2)}/mo
                            </div>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>

                  {/* CTA Buttons */}
                  <div className="p-6 border-t border-gray-200 bg-gradient-to-b from-transparent to-gray-50">
                    <div className={`grid grid-cols-1 ${vpsPlans.length >= 3 ? 'md:grid-cols-3' : `md:grid-cols-${vpsPlans.length}`} gap-4 max-w-4xl mx-auto`}>
                      {vpsPlans.slice(0, 3).map((plan, index) => (
                        <div key={plan.id} className="text-center">
                          <a
                            href="#packages"
                            className={`inline-block w-full px-6 py-3 rounded-lg font-bold transition-all duration-200 ${
                              index === 1 
                                ? 'bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 text-white hover:from-orange-600 hover:via-yellow-600 hover:to-orange-700 shadow-lg relative overflow-hidden group'
                                : index === 0
                                ? 'bg-white border-2 border-gray-300 text-gray-700 hover:border-primary-navy hover:text-primary-navy'
                                : 'bg-primary-navy text-white hover:bg-primary-800'
                            }`}
                          >
                            {index === 1 && (
                              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                            )}
                            <span className={index === 1 ? 'relative z-10' : ''}>Get Started</span>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Additional Features Grid */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              All Plans Include
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
                  title: 'Free SSL Certificate',
                  description: 'Secure your applications with Let\'s Encrypt SSL'
                },
                {
                  icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>,
                  title: 'Full Root Access',
                  description: 'Complete control over your virtual server'
                },
                {
                  icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                  title: 'Instant Deployment',
                  description: 'Server ready in less than 60 seconds'
                },
                {
                  icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
                  title: '24/7 Expert Support',
                  description: 'Get help whenever you need it'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center text-gray-600 mb-4">
                    {feature.icon}
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VPS;